import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';

import {
  TableContainer,
  TableFooter,
  TableV2,
  Tbody,
  Th,
  Td,
  Thead,
  Tr,
  Button,
} from '@frontend/legacy-components';

import { DiscountRow, DiscountRowProps } from './DiscountRow';
import { NewDiscountRow } from './NewDiscountRow';
import type { AnyDiscount, FormValues } from './types';

export const DiscountsTable = () => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const [isAdding, setIsAdding] = useState(false);

  const addDiscount = useCallback(
    (discount) => {
      const newDiscounts = [...values.discounts, discount];
      setIsAdding(false);
      setFieldValue('discounts', newDiscounts);
    },
    [setFieldValue, values.discounts],
  );

  const removeDiscount = useCallback<DiscountRowProps['onRemove']>(
    (id) => {
      const newDiscounts = values.discounts.filter(
        (discount) => discount.id !== id,
      );
      setFieldValue('discounts', newDiscounts);
    },
    [setFieldValue, values.discounts],
  );

  const editDiscount = useCallback<DiscountRowProps['onEdit']>(
    (id, data) => {
      const newDiscounts = values.discounts.map((discount) =>
        discount.id === id
          ? ({ ...discount, ...data } as AnyDiscount)
          : discount,
      );
      setFieldValue('discounts', newDiscounts);
    },
    [setFieldValue, values.discounts],
  );

  return (
    <TableContainer flexShrink={0}>
      <TableV2>
        <Thead>
          <Tr>
            <Th>Discount</Th>
            <Th w={22}>Percent (All Qtys)</Th>
          </Tr>
        </Thead>
        <Tbody>
          {!values.discounts.length && !isAdding && (
            <Tr>
              <Td colSpan={2} textAlign="center" fontWeight="heavy">
                No discounts
              </Td>
            </Tr>
          )}
          {values.discounts.map((discount) => (
            <DiscountRow
              key={discount.id}
              discount={discount}
              onEdit={editDiscount}
              onRemove={removeDiscount}
            />
          ))}
          {isAdding && (
            <NewDiscountRow
              onClose={() => setIsAdding(false)}
              onAdd={addDiscount}
            />
          )}
        </Tbody>
      </TableV2>
      <TableFooter bg="page.plateLight">
        <Button
          size="sm"
          isDisabled={!values.action || isAdding}
          onClick={() => setIsAdding(true)}
        >
          Add Discount
        </Button>
      </TableFooter>
    </TableContainer>
  );
};
